import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Shield } from "lucide-react";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { authAPI } from "../services/api";
import toast from "react-hot-toast";
import styles from "./LoginPage.module.css";

const MfaSetupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [setupData, setSetupData] = useState(null);

  const handleStep1 = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await authAPI.mfaSetupInit(
        email.trim().toLowerCase(),
        password,
      );
      const data = res?.data ?? res;
      if (data?.mfa_setup_token && (data?.qr_url || data?.secret_base32)) {
        setSetupData(data);
        setStep(2);
      } else {
        setError(data?.message || "Failed to start MFA setup.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Invalid email or password.";
      setError(msg);
      if (err.response?.data?.error === "MFA_NOT_AVAILABLE") {
        setError("MFA is not enabled in your organisation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    if (!setupData?.mfa_setup_token || !code.trim()) {
      setError("Enter the code from your authenticator app.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await authAPI.mfaSetupConfirm(setupData.mfa_setup_token, code.trim());
      toast.success("MFA enabled successfully. You can now log in.");
      navigate("/login");
    } catch (err) {
      setError(
        err.response?.data?.message || "Invalid code. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    return (
      <div className={styles.page}>
        <div className={styles.modalWrap}>
          <div className={styles.modal} style={{ position: "absolute" }}>
            <div className="w-full max-w-[420px] mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-[#333333] mb-1">
                  MFA Setup
                </h1>
                <p className="text-[#828282] text-base">
                  Sign in to set up your authenticator app
                </p>
              </div>
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}
              <form onSubmit={handleStep1} className="space-y-5">
                <div>
                  <Label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium text-[#374151]"
                  >
                    Email
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    autoComplete="email"
                    leftIcon={<Mail className="w-5 h-5 text-[#9ca3af]" />}
                    className="bg-[#f9fafb] border-[#e5e7eb] rounded-lg h-12"
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="password"
                    className="mb-2 block text-sm font-medium text-[#374151]"
                  >
                    Password
                  </Label>
                  <Input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    leftIcon={<Lock className="w-5 h-5 text-[#9ca3af]" />}
                    className="bg-[#f9fafb] border-[#e5e7eb] rounded-lg h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#9B82F6] hover:bg-[#8b72e8] text-white rounded-lg font-medium"
                >
                  {loading ? (
                    <>
                      <InlineLoadSkeleton className="h-4 w-4 mr-2" /> Continue
                    </>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </form>
              <p className="mt-6 text-center text-sm text-[#6b7280]">
                <Link to="/login" className="text-[#5D5FEF] hover:underline">
                  Back to login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.modalWrap}>
        <div className={styles.modal} style={{ position: "absolute" }}>
          <div className="w-full max-w-[420px] mx-auto">
            <div className="mb-6 flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#5D5FEF]" />
              <h1 className="text-xl font-bold text-[#333333]">
                Set up authenticator
              </h1>
            </div>
            <p className="text-sm text-[#828282] mb-4">
              Scan the QR code with your authenticator app (e.g. Google
              Authenticator), or enter the secret key manually. Then enter the
              6-digit code below.
            </p>
            {setupData?.qr_url && (
              <div className="mb-4 flex justify-center">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupData.qr_url)}`}
                  alt="QR Code"
                  className="w-[180px] h-[180px]"
                />
              </div>
            )}
            {setupData?.secret_base32 && (
              <div className="mb-4 p-3 bg-[#f9fafb] rounded-lg">
                <Label className="text-xs text-[#6b7280]">
                  Secret key (Base32)
                </Label>
                <p className="text-sm font-mono mt-1 break-all">
                  {setupData.secret_base32}
                </p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}
            <form onSubmit={handleStep2} className="space-y-4">
              <div>
                <Label
                  htmlFor="code"
                  className="mb-2 block text-sm font-medium text-[#374151]"
                >
                  Authenticator code
                </Label>
                <Input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-12 bg-[#9B82F6] hover:bg-[#8b72e8] text-white rounded-lg font-medium"
              >
                {loading ? (
                  <>
                    <InlineLoadSkeleton className="h-4 w-4 mr-2" /> Verifying
                  </>
                ) : (
                  "Enable MFA"
                )}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-[#6b7280]">
              <Link to="/login" className="text-[#5D5FEF] hover:underline">
                Back to login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaSetupPage;
